/**
 * Tutor filters for the management {@link DataTable}.
 * Each group is a local chip/select block; visibility is allowed ids ∩ visible ids.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { Button } from '@/components/forms/Button';
import { SearchInput } from '@/components/forms/SearchInput';
import { MultiSelect } from '@/components/forms/MultiSelect';
import { Select } from '@/components/forms/Select';
import { DateRangeInput } from '@/components/forms/DateRangeInput';
import { useDepartments } from '@/hooks/useDepartments';
import { useEstablishmentsAutocomplete } from '@/hooks/useEstablishmentsAutocomplete';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  TUTOR_INPERSON_SITUATION_CONFIG,
  TUTOR_INPERSON_STATUS_BY_TAB,
  WEEKDAY_FILTER_OPTIONS,
  INPERSON_EXIT_REASON_OPTIONS,
  type TutorInPersonTabView,
} from '@/components/tutors/tutor-configs';
import type { TutorFilters } from '@/types/inperson/tutor.types';
import { selectedTutorAvailabilityDays } from './tutor-table-utils';
import { getSubjects } from '@/services/subjects.service';
/** Props for {@link TutorFiltersContent}. */
interface TutorFiltersContentProps {
  filters: TutorFilters;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  activeTab?: TutorInPersonTabView;
  onSetSearch?: (value: string | undefined) => void;
  onToggleStatus: (status: string) => void;
  onToggleSubject: (subjectId: string) => void;
  onSetEstablishment: (id: string | undefined) => void;
  onSetDepartmentCodes: (codes: string[] | undefined) => void;
  /** Toggle a weekday chip (multi-select). */
  onToggleAvailability: (day: string) => void;
  onSetEngagementStartDateRange?: (from: string | undefined, to: string | undefined) => void;
  onSetEngagementEndDateRange?: (from: string | undefined, to: string | undefined) => void;
  onSetExitDateRange?: (from: string | undefined, to: string | undefined) => void;
  onSetExitReason?: (reason: string | undefined) => void;
}

/** True when the filter is both allowed for this actor and currently visible. */
function isVisible(
  filterId: string,
  allowedFilterIds: string[],
  visibleFilterIds: string[],
): boolean {
  return allowedFilterIds.includes(filterId) && visibleFilterIds.includes(filterId);
}

/** Debounced name / email / phone search; empty string becomes `undefined`. */
const SearchFilter: React.FC<{
  filters: TutorFilters;
  onSetSearch: (value: string | undefined) => void;
}> = ({ filters, onSetSearch }) => (
  <div className="filter-group">
    <span className="filter-group-label">Recherche</span>
    <div className="w-64">
      <SearchInput
        size="sm"
        shortcut={false}
        debounceMs={300}
        placeholder="Nom, prénom, email ou téléphone..."
        value={filters.search ?? ''}
        onSearch={(q) => onSetSearch(q.trim() || undefined)}
        onClear={() => onSetSearch(undefined)}
      />
    </div>
  </div>
);

/** One status chip entry for the current in-person tab. */
type InPersonStatusEntry = (typeof TUTOR_INPERSON_STATUS_BY_TAB)[TutorInPersonTabView][number];

/** Situation chips for the current tab (`locked` entries stay on). */
const StatusFilter: React.FC<{
  filters: TutorFilters;
  activeTab: TutorInPersonTabView;
  onToggleStatus: (status: string) => void;
}> = ({ filters, activeTab, onToggleStatus }) => {
  const entries = TUTOR_INPERSON_STATUS_BY_TAB[activeTab] ?? [];
  const lockSingleton = entries.length <= 1;

  function renderStatusChip(entry: InPersonStatusEntry): React.ReactNode {
    const config = TUTOR_INPERSON_SITUATION_CONFIG[entry.key];
    if (!config) return null;
    const locked = !!entry.locked || lockSingleton;
    const Icon = config.icon;
    const isActive = locked || filters.statuses.includes(entry.key);

    function handleClick(): void {
      if (!locked) onToggleStatus(entry.key);
    }

    return (
      <Button
        key={entry.key}
        variant="custom"
        size="sm"
        icon={Icon}
        iconPosition="left"
        disabled={locked}
        className={cn(
          'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
          isActive ? 'filter-badge-active' : 'filter-badge-inactive',
          locked && 'opacity-60 cursor-not-allowed',
        )}
        style={
          {
            '--filter-color': config.badgeColor ?? 'rgb(var(--status-success))',
          } as React.CSSProperties
        }
        onClick={handleClick}
      >
        {config.label}
      </Button>
    );
  }

  return (
    <div className="filter-group">
      <span className="filter-group-label">Statut</span>
      <div className="filter-options">{entries.map(renderStatusChip)}</div>
    </div>
  );
};

/** Subject chips (first page of the catalog). */
const SubjectFilter: React.FC<{
  filters: TutorFilters;
  onToggleSubject: (subjectId: string) => void;
}> = ({ filters, onToggleSubject }) => {
  const { data: subjects = [] } = useQuery({
    queryKey: ['subjects', 'tutor-filters'],
    queryFn: async () => {
      const response = await getSubjects({ page: 1, pageSize: 20 });
      return response.data;
    },
  });

  return (
    <div className="filter-group">
      <span className="filter-group-label">Matières</span>
      <div className="filter-options max-h-48 overflow-y-auto pr-1">
        {subjects.map((subject) => {
          const isActive = filters.subjects.includes(subject.id);
          return (
            <Button
              key={subject.id}
              variant="custom"
              size="sm"
              icon={BookOpen}
              iconPosition="left"
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                isActive ? 'filter-option-primary--active' : 'filter-option-primary',
              )}
              onClick={() => onToggleSubject(subject.id)}
            >
              {subject.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

/** Establishment {@link Autocomplete} (min 2 chars). Empty value clears the filter. */
const EstablishmentFilter: React.FC<{
  filters: TutorFilters;
  onSetEstablishment: (id: string | undefined) => void;
}> = ({ filters, onSetEstablishment }) => {
  const { options, isLoading, onSearch, onLoadMore, hasMore } = useEstablishmentsAutocomplete();

  return (
    <div className="filter-group">
      <span className="filter-group-label">Établissement</span>
      <div className="w-64">
        <Autocomplete
          placeholder="Rechercher un établissement..."
          options={options}
          value={filters.establishmentId ?? ''}
          onChange={(val) => onSetEstablishment(val || undefined)}
          onSearch={onSearch}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoading={isLoading}
          minSearchLength={2}
          inputSize="sm"
        />
      </div>
    </div>
  );
};

/** Assignment-department {@link MultiSelect} filter. */
const DepartmentFilter: React.FC<{
  filters: TutorFilters;
  onSetDepartmentCodes: (codes: string[] | undefined) => void;
}> = ({ filters, onSetDepartmentCodes }) => {
  const { data: departments = [], error: departmentsError } = useDepartments('FR');

  const options = React.useMemo(
    () => departments.map((dept) => ({ value: dept.code, label: dept.name })),
    [departments],
  );

  function handleDepartmentCodesChange(values: string[]): void {
    onSetDepartmentCodes(values.length > 0 ? values : undefined);
  }

  return (
    <div className="filter-group">
      <span className="filter-group-label">Département d'affectation</span>
      <div className="w-64">
        <MultiSelect
          placeholder="Sélectionner des départements..."
          options={options}
          value={filters.departmentCodes ?? []}
          onChange={handleDepartmentCodesChange}
          inputSize="sm"
          error={departmentsError ? getErrorMessage(departmentsError) : undefined}
        />
      </div>
    </div>
  );
};

/** Weekday chips (multi-select). */
const AvailabilityFilter: React.FC<{
  filters: TutorFilters;
  onToggleAvailability: (day: string) => void;
}> = ({ filters, onToggleAvailability }) => {
  /** Normalized weekday keys from the filter bag (legacy single-string support). */
  const selectedDays = selectedTutorAvailabilityDays(filters);

  return (
    <div className="filter-group">
      <span className="filter-group-label">Disponibilité</span>
      <div className="filter-options">
        {WEEKDAY_FILTER_OPTIONS.map((day) => {
          const isActive = selectedDays.includes(day.value);
          return (
            <Button
              key={day.value}
              variant="custom"
              size="sm"
              icon={Calendar}
              iconPosition="left"
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                isActive ? 'filter-option-secondary--active' : 'filter-option-secondary',
              )}
              onClick={() => onToggleAvailability(day.value)}
            >
              {day.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

/** Engagement-start date range (empty string → `undefined`). */
const EntryDateFilter: React.FC<{
  filters: TutorFilters;
  onSetEngagementStartDateRange: (from: string | undefined, to: string | undefined) => void;
}> = ({ filters, onSetEngagementStartDateRange }) => {
  /** Keep `to` and write `from` (blank clears). */
  const handleFromChange = (val: string) => {
    onSetEngagementStartDateRange(val || undefined, filters.engagementStartDateTo);
  };

  /** Keep `from` and write `to` (blank clears). */
  const handleToChange = (val: string) => {
    onSetEngagementStartDateRange(filters.engagementStartDateFrom, val || undefined);
  };

  return (
    <div className="filter-group">
      <span className="filter-group-label">Date d'entrée</span>
      <div className="w-auto">
        <DateRangeInput
          startDate={filters.engagementStartDateFrom}
          endDate={filters.engagementStartDateTo}
          onStartDateChange={handleFromChange}
          onEndDateChange={handleToChange}
          layout="horizontal"
          showConnector={true}
          showPresets={false}
          hidePickerLabels={true}
          compact={true}
        />
      </div>
    </div>
  );
};

/** Exit / deactivation date range (empty string → `undefined`). */
const ExitDateFilter: React.FC<{
  filters: TutorFilters;
  onSetExitDateRange: (from: string | undefined, to: string | undefined) => void;
}> = ({ filters, onSetExitDateRange }) => {
  /** Keep `to` and write `from` (blank clears). */
  const handleFromChange = (val: string) => {
    onSetExitDateRange(val || undefined, filters.exitDateTo);
  };

  /** Keep `from` and write `to` (blank clears). */
  const handleToChange = (val: string) => {
    onSetExitDateRange(filters.exitDateFrom, val || undefined);
  };

  return (
    <div className="filter-group">
      <span className="filter-group-label">Date de désactivation</span>
      <div className="w-auto">
        <DateRangeInput
          startDate={filters.exitDateFrom}
          endDate={filters.exitDateTo}
          onStartDateChange={handleFromChange}
          onEndDateChange={handleToChange}
          layout="horizontal"
          showConnector={true}
          showPresets={false}
          hidePickerLabels={true}
          compact={true}
        />
      </div>
    </div>
  );
};

/** Engagement-end date range (empty string → `undefined`). */
const ContractEndDateFilter: React.FC<{
  filters: TutorFilters;
  onSetEngagementEndDateRange: (from: string | undefined, to: string | undefined) => void;
}> = ({ filters, onSetEngagementEndDateRange }) => {
  /** Keep `to` and write `from` (blank clears). */
  const handleFromChange = (val: string) => {
    onSetEngagementEndDateRange(val || undefined, filters.engagementEndDateTo);
  };

  /** Keep `from` and write `to` (blank clears). */
  const handleToChange = (val: string) => {
    onSetEngagementEndDateRange(filters.engagementEndDateFrom, val || undefined);
  };

  return (
    <div className="filter-group">
      <span className="filter-group-label">Date de fin engagement</span>
      <div className="w-auto">
        <DateRangeInput
          startDate={filters.engagementEndDateFrom}
          endDate={filters.engagementEndDateTo}
          onStartDateChange={handleFromChange}
          onEndDateChange={handleToChange}
          layout="horizontal"
          showConnector={true}
          showPresets={false}
          hidePickerLabels={true}
          compact={true}
        />
      </div>
    </div>
  );
};

/** Exit-reason {@link Select} (empty value = all reasons). */
const ExitReasonFilter: React.FC<{
  filters: TutorFilters;
  onSetExitReason: (reason: string | undefined) => void;
}> = ({ filters, onSetExitReason }) => (
  <div className="filter-group">
    <span className="filter-group-label">Motif de sortie</span>
    <div className="w-64">
      <Select
        key={filters.exitReason ?? '__empty__'}
        options={[...INPERSON_EXIT_REASON_OPTIONS]}
        value={filters.exitReason ?? ''}
        onChange={(val) => onSetExitReason(val || undefined)}
        placeholder="Tous les motifs"
        size="sm"
      />
    </div>
  </div>
);

/** Filter groups shown according to allowed + visible ids. */
export const TutorFiltersContent: React.FC<TutorFiltersContentProps> = ({
  filters,
  allowedFilterIds,
  visibleFilterIds,
  activeTab = 'tutors',
  onSetSearch,
  onToggleStatus,
  onToggleSubject,
  onSetEstablishment,
  onSetDepartmentCodes,
  onToggleAvailability,
  onSetEngagementStartDateRange,
  onSetEngagementEndDateRange,
  onSetExitDateRange,
  onSetExitReason,
}) => {
  const show = (id: string) => isVisible(id, allowedFilterIds, visibleFilterIds);

  return (
    <div className="filter-groups-container">
      {show('filter-search') && onSetSearch && (
        <SearchFilter filters={filters} onSetSearch={onSetSearch} />
      )}
      {show('filter-status') && (
        <StatusFilter filters={filters} activeTab={activeTab} onToggleStatus={onToggleStatus} />
      )}
      {show('filter-subject') && (
        <SubjectFilter filters={filters} onToggleSubject={onToggleSubject} />
      )}
      {show('filter-establishment') && (
        <EstablishmentFilter filters={filters} onSetEstablishment={onSetEstablishment} />
      )}
      {show('filter-department') && (
        <DepartmentFilter filters={filters} onSetDepartmentCodes={onSetDepartmentCodes} />
      )}
      {show('filter-availability') && (
        <AvailabilityFilter filters={filters} onToggleAvailability={onToggleAvailability} />
      )}
      {show('filter-entry-date') && (
        <EntryDateFilter
          filters={filters}
          onSetEngagementStartDateRange={onSetEngagementStartDateRange!}
        />
      )}
      {show('filter-contract-end-date') && onSetEngagementEndDateRange && (
        <ContractEndDateFilter
          filters={filters}
          onSetEngagementEndDateRange={onSetEngagementEndDateRange}
        />
      )}
      {show('filter-exit-date') && (
        <ExitDateFilter filters={filters} onSetExitDateRange={onSetExitDateRange!} />
      )}
      {show('filter-exit-reason') && (
        <ExitReasonFilter filters={filters} onSetExitReason={onSetExitReason!} />
      )}
    </div>
  );
};
